import { useEffect, useState } from 'react'
import {
  FieldValues,
  Path,
  PathValue,
  SubmitHandler,
  useForm,
  UseFormSetValue
} from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Loader,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack } from '@mui/material'
import { DataService } from 'api/DataService'
import * as yup from 'yup'

import InputController from 'components/InputController'

import { toastFirstError } from 'utils/yupResolverWithToast'

export const schemaCategoryForm = yup.object({
  name: yup.string().required('Nome é obrigatório')
})

export type CategoryFormSchema = yup.InferType<typeof schemaCategoryForm>

type CategoryFormProps<T extends FieldValues> = {
  setValueFromOutside?: UseFormSetValue<T>
  onCreated?: (item: { uuid: string; name: string }) => void
  handleClose: () => void
  uuid?: string
  defaultName?: string
}

const CategoryForm = <T extends FieldValues>({
  setValueFromOutside,
  onCreated,
  handleClose,
  uuid,
  defaultName
}: CategoryFormProps<T>) => {
  const [loading, setLoading] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = useForm<CategoryFormSchema>({
    defaultValues: { name: defaultName },
    resolver: yupResolver(schemaCategoryForm)
  })

  const [dialogOpen, setDialogOpen] = useState(false)

  const handleClickToSubmit: SubmitHandler<CategoryFormSchema> = async () => {
    setDialogOpen(true)
  }

  const handleSubmitForm: SubmitHandler<CategoryFormSchema> = async (
    dataForm
  ) => {
    setLoading(true)

    const data = {
      name: dataForm.name
    }

    // O await é obrigatório: sem ele o setLoading(false) executa antes da resposta,
    // reabilitando o botão enquanto a requisição está em voo e permitindo duplo submit
    // (dois POSTs concorrentes passam pela validação de uniqueness e o segundo estoura
    // o índice único no banco).
    try {
      await toast.promise(
        async () => {
          const response = await DataService({
            data: data,
            type: uuid ? 'PATCH' : 'POST',
            url: uuid
              ? `/v1/accounts_payables/categories/${uuid}`
              : '/v1/accounts_payables/categories'
          })

          if (response.error) {
            setDialogOpen(false)
            throw new Error(
              response.error && (response.detail_error || response.message)
            )
          } else {
            // Com onCreated quem abriu o formulário decide onde gravar o item novo
            // (ex.: allocations.N.category); sem ele, grava no campo de raiz 'category'.
            if (onCreated) {
              onCreated({
                uuid: response.data.record.uuid,
                name: response.data.record.name
              })
            } else if (setValueFromOutside) {
              setValueFromOutside(
                'category' as Path<T>,
                {
                  label: response.data.record.name,
                  value: response.data.record.uuid
                } as PathValue<T, Path<T>>
              )
            }

            setDialogOpen(false)
            handleClose()
            return response.data.message
          }
        },
        {
          pending: `${uuid ? 'Atualizando' : 'Criando'} categoria`,
          success: {
            render({ data }) {
              return `${data}`
            }
          },
          error: {
            render({ data }) {
              return `${(data as any)?.message}`
            }
          }
        }
      )
    } catch {
      // erro já exibido pelo toast.promise
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!uuid) return

    const getCategory = async () => {
      setLoading(true)
      const response = await DataService({
        type: 'GET',
        url: `/v1/accounts_payables/categories/${uuid}`
      })

      const record = response.data.record

      reset({
        name: record.name
      })

      setLoading(false)
    }

    getCategory()
  }, [uuid])

  if (loading) {
    return <Loader />
  }

  return (
    <Stack justifyContent="space-between" flexGrow={1} gap="22px">
      <InputController control={control} name="name">
        <Textfield label="Nome" placeholder="Digite o nome da categoria" />
      </InputController>

      <Stack direction="row" gap={1}>
        <Button fullWidth variantButton="neutral" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={loading || (!isDirty && !defaultName)}
          onClick={handleSubmit(handleClickToSubmit, toastFirstError)}
          variantButton="primary"
        >
          {uuid ? 'Atualizar' : 'Salvar'}
        </Button>
      </Stack>

      <Dialog
        icon={uuid ? 'edit' : 'add'}
        title="Categoria"
        description={
          uuid
            ? 'Confirma atualizar essa Categoria?'
            : 'Confirma criar uma Categoria?'
        }
        setIsOpenDialog={setDialogOpen}
        isOpenDialog={dialogOpen}
        cancelButton
      >
        <Button
          autoFocus
          disabled={loading}
          loading={loading}
          onClick={handleSubmit(handleSubmitForm, toastFirstError)}
          fullWidth
        >
          {uuid ? 'Atualizar' : 'Criar'}
        </Button>
      </Dialog>
    </Stack>
  )
}

export default CategoryForm
